import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import NavigationBar from '../../components/ui/NavigationBar'
import Icon from '../../components/AppIcon'
import Button from '../../components/ui/Button'
import { getCertificate } from '../../utils/certHelpers'

const CertificateVerify = () => {
  const { certId } = useParams()
  const [certificate, setCertificate] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const { certificate: certData } = await getCertificate(certId)
      setCertificate(certData)
      setLoading(false)
    }
    load()
  }, [certId])

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Verifying certificate...</span>
        </div>
      </div>
    )
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <Icon name="ShieldAlert" size={40} className="text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Certificate Not Found</h1>
          <p className="text-muted-foreground mb-6">
            No certificate matches ID <span className="font-mono">{certId}</span>. It may have been mistyped or doesn't exist.
          </p>
          <Link to="/course-catalog">
            <Button variant="default">Browse Courses</Button>
          </Link>
        </div>
      </div>
    )
  }

  const isValid = certificate?.status === 'valid'
  const issueDate = certificate?.issueDate
    ? new Date(certificate.issueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Unknown'

  return (
    <>
      <Helmet>
        <title>Certificate Verification - {certificate?.certId} - Nesolearn</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-6">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                isValid ? 'bg-success/10' : 'bg-destructive/10'
              }`}
            >
              <Icon name={isValid ? 'ShieldCheck' : 'ShieldX'} size={30} className={isValid ? 'text-success' : 'text-destructive'} />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-1">
              {isValid ? 'Certificate Verified' : 'Certificate Revoked'}
            </h1>
            <p className="text-muted-foreground text-sm">
              This certificate is {isValid ? 'valid and was issued by a real organization on Nesolearn.' : 'no longer valid.'}
            </p>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="bg-gradient-to-br from-accent/90 to-accent/60 p-8 text-center text-white">
              <Icon name="Award" size={36} className="mx-auto mb-3" />
              <p className="text-xs uppercase tracking-widest text-white/80 mb-1">Certificate of Completion</p>
              <h2 className="text-xl font-bold">{certificate?.courseTitle}</h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="text-sm text-muted-foreground">Issued To</span>
                <span className="text-sm font-semibold text-foreground">{certificate?.issuedTo}</span>
              </div>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="text-sm text-muted-foreground">Issued By</span>
                <span className="text-sm font-semibold text-foreground flex items-center gap-1">
                  {certificate?.issuedBy}
                  {certificate?.issuedByVerified && <Icon name="BadgeCheck" size={14} className="text-accent" />}
                </span>
              </div>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="text-sm text-muted-foreground">Date Issued</span>
                <span className="text-sm font-semibold text-foreground">{issueDate}</span>
              </div>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="text-sm text-muted-foreground">Score</span>
                <span className="text-sm font-semibold text-foreground">{certificate?.score}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Certificate ID</span>
                <span className="text-sm font-mono font-semibold text-foreground">{certificate?.certId}</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Powered by Nesolearn — the operating system for institutions delivering future-ready digital education.
          </p>
        </main>
      </div>
    </>
  )
}

export default CertificateVerify